import { defineMessage, MessageDescriptor } from 'react-intl';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { faFileCheck } from '@fortawesome/pro-regular-svg-icons/faFileCheck';
import { faMessage } from '@fortawesome/pro-regular-svg-icons/faMessage';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';
import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons/faArrowRotateLeft';
import {
  DashboardContextId,
  DashboardPromptId,
} from '../../app/_lib/backend/aiContext/ids';
import { OAIMode } from './oai';

export type DashboardChipDefinition = {
  id: DashboardPromptId;
  label: MessageDescriptor;
  /** Starter message sent to the chat. May reference `{topic}` (the most recently watched video). */
  prompt: MessageDescriptor;
  icon: IconDefinition;
  mode: OAIMode;
};

/**
 * The dashboard example prompt chips.
 */
export const DASHBOARD_PROMPTS: Record<
  DashboardPromptId,
  DashboardChipDefinition
> = {
  [DashboardContextId.StudyPlan]: {
    id: DashboardContextId.StudyPlan,
    label: defineMessage({
      id: 'dashboard.prompt-chip.study-plan.label',
      defaultMessage: 'Help me build a study plan',
    }),
    prompt: defineMessage({
      id: 'dashboard.prompt-chip.study-plan.prompt',
      defaultMessage: 'Create a study schedule',
    }),
    icon: faFileLines,
    mode: OAIMode.DefaultStudy,
  },
  [DashboardContextId.QuizWatched]: {
    id: DashboardContextId.QuizWatched,
    label: defineMessage({
      id: 'dashboard.prompt-chip.quiz-watched.label',
      defaultMessage: 'Quiz me on what I just watched',
    }),
    prompt: defineMessage({
      id: 'dashboard.prompt-chip.quiz-watched.prompt',
      defaultMessage:
        'Create 3 board-style, one-best-answer questions on "{topic}". Use short clinical vignettes that test application, not simple recall.',
    }),
    icon: faFileCheck,
    mode: OAIMode.DefaultStudy,
  },
  [DashboardContextId.TutorWatched]: {
    id: DashboardContextId.TutorWatched,
    label: defineMessage({
      id: 'dashboard.prompt-chip.tutor-watched.label',
      defaultMessage: 'Tutor me on what I just watched',
    }),
    prompt: defineMessage({
      id: 'dashboard.prompt-chip.tutor-watched.prompt',
      defaultMessage: 'Tutor me on "{topic}".',
    }),
    icon: faMessage,
    mode: OAIMode.Tutor,
  },
  [DashboardContextId.PracticeQuestion]: {
    id: DashboardContextId.PracticeQuestion,
    label: defineMessage({
      id: 'dashboard.prompt-chip.practice-question.label',
      defaultMessage: 'Talk me through a recently missed practice question',
    }),
    prompt: defineMessage({
      id: 'dashboard.prompt-chip.practice-question.prompt',
      defaultMessage: `Explain pitfalls of this MCQ below, then present variations on the objective.
        Question Topic: {questionTopic};
        Question stem: {questionStem};
        Answer options: {answerOptions}`,
    }),
    icon: faCircleQuestion,
    mode: OAIMode.DefaultStudy,
  },
  [DashboardContextId.MissedQuestions]: {
    id: DashboardContextId.MissedQuestions,
    label: defineMessage({
      id: 'dashboard.prompt-chip.missed-questions.label',
      defaultMessage: 'Tutor me on missed questions',
    }),
    prompt: defineMessage({
      id: 'dashboard.prompt-chip.missed-questions.prompt',
      defaultMessage: `Tutor me on this missed question: 
        Question Topic: {questionTopic};
        Question stem: {questionStem};
        Answer options: {answerOptions}`,
    }),
    icon: faArrowRotateLeft,
    mode: OAIMode.Tutor,
  },
};
